import { useState } from "react";
import { ArrowRight, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ProjectColor } from "@/features/board/types";
import { ProjectDialog } from "@/features/projects/ProjectDialog";
import { useProjects } from "@/features/projects/useProjects";

const colorClasses: Record<ProjectColor, string> = {
  rose: "bg-primary",
  violet: "bg-violet-500",
  blue: "bg-blue-500",
  teal: "bg-teal-500",
  amber: "bg-amber-500",
};

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, loading, error, load, createProject } = useProjects();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <section className="py-8 sm:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <p className="font-mono text-sm font-medium tracking-wide text-primary-strong">WORKSPACE</p>
            <h1 className="text-balance pt-2 text-3xl font-semibold tracking-tight text-neutral-950 sm:text-4xl">
              Projects
            </h1>
            <p className="text-pretty max-w-[60ch] pt-2 text-base text-neutral-600 sm:text-sm">
              Keep work visible, focused, and moving across the team.
            </p>
          </div>
          <Button className="pl-2 pr-3" onClick={() => setDialogOpen(true)} type="button">
            <Plus className="size-4 shrink-0 stroke-current" />
            New project
          </Button>
        </header>

        {error ? (
          <Alert className="mt-6 flex items-center justify-between gap-3 border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">
            <span>{error}</span>
            <Button onClick={() => void load()} size="sm" type="button" variant="ghost">
              Retry
            </Button>
          </Alert>
        ) : null}

        <div className="@container pt-8">
          {loading ? (
            <ul className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3" role="list">
              {[0, 1, 2].map((item) => (
                <li className="h-48 animate-pulse rounded-xl bg-neutral-200/70" key={item} />
              ))}
            </ul>
          ) : projects.length ? (
            <ul className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3" role="list">
              {projects.map((project) => (
                <li key={project.id}>
                  <Link
                    className="group flex min-h-48 flex-col rounded-xl bg-white p-5 shadow-sm ring-1 ring-neutral-950/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    to={`/projects/${project.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span aria-hidden="true" className={`size-3 shrink-0 rounded-full ${colorClasses[project.color]}`} />
                      <Badge variant="secondary">{project.role === "owner" ? "Owner" : "Member"}</Badge>
                    </div>
                    <div className="min-w-0 flex-1 pt-5">
                      <h2 className="truncate text-balance text-xl font-semibold text-neutral-950">
                        {project.name}
                      </h2>
                      <p className="text-pretty line-clamp-2 pt-2 text-base text-neutral-600 sm:text-sm">
                        {project.description || "A focused kanban workspace for this project."}
                      </p>
                    </div>
                    <div className="flex items-end justify-between gap-4 pt-5">
                      <p className="tabular-nums text-base text-neutral-500 sm:text-sm">
                        {project.cardCount} {project.cardCount === 1 ? "card" : "cards"} · {project.memberCount}{" "}
                        {project.memberCount === 1 ? "member" : "members"}
                      </p>
                      <ArrowRight className="size-4 shrink-0 stroke-neutral-400 group-hover:translate-x-0.5 group-hover:stroke-neutral-950 group-focus-visible:translate-x-0.5" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-950/15 bg-white px-6 py-16 text-center">
              <h2 className="text-balance text-xl font-semibold text-neutral-950">Your first board starts here</h2>
              <p className="text-pretty mx-auto max-w-[48ch] pt-2 text-base text-neutral-600 sm:text-sm">
                Create a project and WAW Board will prepare a clean workflow you can use immediately.
              </p>
              <Button className="mt-5" onClick={() => setDialogOpen(true)} type="button" variant="secondary">
                Create a project
              </Button>
            </div>
          )}
        </div>
      </div>

      <ProjectDialog
        onClose={() => setDialogOpen(false)}
        onCreate={async (input) => {
          const project = await createProject(input);
          navigate(`/projects/${project.id}`);
          return project;
        }}
        open={dialogOpen}
      />
    </section>
  );
};
